import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Loader } from '@mantine/core'
import { IconPaperclip } from '@tabler/icons-react'
import { normalizeErrorString } from '@medplum/core'
import type { Practitioner } from '@medplum/fhirtypes'
import { useMedplumProfile } from '@medplum/react'
import { useSubscription } from '@medplum/react-hooks'
import { showNotification } from '@mantine/notifications'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { formatDateTime } from '@/utils'
import {
  checkThreadStatus,
  getCommunicationFromSubscriptionBundle,
  isArchivedThread,
  type MessageThreadSummary,
  type PractitionerThreadOption,
  type ThreadMessage,
} from '@/pages/Messages/service'
import { ThreadMessageComposer } from '@/pages/Messages/components/ThreadMessageComposer'
import type { MiddlewareThreadSummary } from '../service'

type ThreadPaneProps = {
  threadSummary: MiddlewareThreadSummary
  onThreadRead?: (threadId: string) => void
}

export const ThreadPane = ({ threadSummary, onThreadRead }: ThreadPaneProps) => {
  const middleware = useMiddlewareClient()
  const profile = useMedplumProfile()
  const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined

  const [messages, setMessages] = useState<ThreadMessage[]>([])
  const [loading, setLoading] = useState(false)
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const shouldScrollRef = useRef(false)
  const threadSummaryRef = useRef(threadSummary)

  useEffect(() => {
    threadSummaryRef.current = threadSummary
  }, [threadSummary])

  // Initial load + mark read — same BL as ThreadDetails.tsx
  useEffect(() => {
    const threadId = threadSummary.thread.id
    if (!threadId) return

    let cancelled = false
    setLoading(true)
    setMessages([])

    middleware
      .get('/threads/messages', { threadId, count: 500 })
      .then(async (result) => {
        if (cancelled) return
        const msgs = result as ThreadMessage[]
        setMessages(msgs)
        shouldScrollRef.current = true

        const latestMessage = msgs[msgs.length - 1]?.message
        const currentThread = threadSummaryRef.current?.thread
        if (
          practitionerProfile?.id &&
          currentThread &&
          latestMessage &&
          checkThreadStatus(currentThread, practitionerProfile.id, latestMessage) === 'unread'
        ) {
          await middleware.post('/threads/read', { threadId })
          onThreadRead?.(threadId)
        }
      })
      .catch((err) => {
        if (!cancelled) showNotification({ color: 'red', message: normalizeErrorString(err) })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [middleware, practitionerProfile?.id, threadSummary.thread.id])

  // Scroll within the messages container only — never moves the page
  useLayoutEffect(() => {
    if (!shouldScrollRef.current) return
    const container = messagesContainerRef.current
    if (container) container.scrollTop = container.scrollHeight
    shouldScrollRef.current = false
  }, [messages])

  // Live subscription — identical to ThreadDetails.tsx
  useSubscription(
    threadSummary.thread.id
      ? `Communication?part-of=Communication/${threadSummary.thread.id}`
      : undefined,
    (bundle) => {
      const communication = getCommunicationFromSubscriptionBundle(bundle)
      if (!communication?.id) return

      setMessages((prev) => {
        const existingIndex = prev.findIndex((m) => m.message.id === communication.id)
        const knownParticipants = [threadSummary.sender, ...threadSummary.recipients].filter(
          (p): p is PractitionerThreadOption => Boolean(p?.practitioner?.id),
        )
        const sender = knownParticipants.find(
          (p) => `Practitioner/${p.practitioner.id}` === communication.sender?.reference,
        )
        const recipients = (communication.recipient ?? [])
          .map((r) =>
            knownParticipants.find((p) => `Practitioner/${p.practitioner.id}` === r.reference),
          )
          .map((p) => p?.practitioner)
          .filter((p): p is Practitioner => Boolean(p))

        const nextItem: ThreadMessage = {
          message: communication,
          sender: sender?.practitioner,
          recipients,
        }

        if (existingIndex >= 0) {
          return prev.map((item, i) => (i === existingIndex ? { ...item, ...nextItem } : item))
        }

        shouldScrollRef.current = true
        return [...prev, nextItem]
      })
    },
  )

  // No optimistic local add — the subscription handles delivery for all
  // senders including the current user. This avoids the race where the
  // WebSocket event arrives while `createThreadMessage` is still awaiting
  // `updateThreadReadState`, before we've had a chance to register the ID.
  const handleMessageCreated = () => {
    // Just scroll — the message will appear via the subscription within ~50ms.
    shouldScrollRef.current = true
    const container = messagesContainerRef.current
    if (container) container.scrollTop = container.scrollHeight
  }

  const threadSummaryCast = threadSummary as MessageThreadSummary

  return (
    <div className="border-b border-gray-100 bg-white">
      <div
        ref={messagesContainerRef}
        className="max-h-[280px] overflow-y-auto bg-gray-50 px-4 py-3"
      >
        {loading && (
          <div className="flex justify-center py-4">
            <Loader size="sm" color="gray" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <p className="text-xs text-gray-400">No messages yet.</p>
        )}
        {!loading &&
          messages.map((msg) => {
            const content = msg.message.payload?.[0]?.contentString
            const attachment = msg.message.payload?.[0]?.contentAttachment
            if (!content && !attachment?.url) return null

            const isOwn =
              msg.message.sender?.reference === `Practitioner/${practitionerProfile?.id}`
            const senderName =
              !isOwn && msg.sender
                ? (msg.sender.name?.[0]?.text ??
                  [msg.sender.name?.[0]?.given?.join(' '), msg.sender.name?.[0]?.family]
                    .filter(Boolean)
                    .join(' '))
                : null
            const time = msg.message.sent ? formatDateTime(msg.message.sent, '-', true) : ''

            return (
              <div
                key={msg.message.id}
                className={`mb-3 flex last:mb-0 ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}
                >
                  {senderName && (
                    <span className="px-1 text-[11px] font-medium text-gray-500">{senderName}</span>
                  )}
                  <div
                    className={`rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                      isOwn
                        ? 'rounded-br-sm text-white'
                        : 'rounded-bl-sm border border-gray-200 bg-white text-gray-800'
                    }`}
                    style={isOwn ? { backgroundColor: 'rgb(229, 83, 61)' } : undefined}
                  >
                    {content}
                    {attachment?.url && (
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noreferrer"
                        className={`${content ? 'mt-2' : ''} inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs underline ${
                          isOwn ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <IconPaperclip size={12} />
                        {attachment.title || 'Open attachment'}
                      </a>
                    )}
                  </div>
                  <span className="px-1 text-[10px] text-gray-400">{time}</span>
                </div>
              </div>
            )
          })}
      </div>

      {!isArchivedThread(threadSummary.thread) && (
        <div className="border-t border-gray-100">
          <ThreadMessageComposer
            threadSummary={threadSummaryCast}
            onCreated={handleMessageCreated}
          />
        </div>
      )}
    </div>
  )
}
